import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import assertNever from "assert-never";
import React from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import {
  TOAST_DISMISS_ANIMATION_DURATION,
  TOAST_ENTER_ANIMATION_DURATION,
} from "../../hooks/useToaster";
import { ToastsOverlay } from "../Generic/ToastsOverlay";
import Toast from "./Toast";
import useToastConsumer from "./useToastConsumer";

const animateIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const animateOut = keyframes`
from {
  opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const StyledToastsContainer = styled.div`
  position: fixed;
  z-index: 1;
  top: 0;
  width: 100%;
  box-sizing: border-box;

  > * {
    margin: calc(env(safe-area-inset-top) + 16px) 16px 16px;
  }
`;

const AnimatedToastWrapper = styled.div<{ status: TransitionStatus }>`
  transition: all 100ms ease;

  ${(props) => {
    switch (props.status) {
      case "unmounted":
        return css`
          margin-bottom: -25%;
        `;
      case "entered":
      case "entering":
        return css`
          animation: ${animateIn} ${TOAST_ENTER_ANIMATION_DURATION}ms ease;
        `;
      case "exited":
      case "exiting":
        return css`
          animation: ${animateOut} ${TOAST_DISMISS_ANIMATION_DURATION}ms ease;
        `;
      default:
        assertNever(props.status);
    }
  }}
`;

const ToastsContainer = () => {
  const toasts = useToastConsumer();
  const nodeRef = React.useRef(null);

  return (
    <ToastsOverlay>
      <StyledToastsContainer>
        {[...toasts.state].reverse().map((invocation) => (
          <Transition
            key={invocation.key}
            in={!invocation.isDismissing}
            timeout={invocation.timeout ?? 120000}
            unmountOnExit={true}
            nodeRef={nodeRef}
          >
            {(transitionStatus) => (
              <AnimatedToastWrapper status={transitionStatus}>
                <Toast ref={nodeRef} {...invocation.toast} />
              </AnimatedToastWrapper>
            )}
          </Transition>
        ))}
      </StyledToastsContainer>
    </ToastsOverlay>
  );
};

export default ToastsContainer;
